import moment from 'moment-timezone'

/** Default series end (from start date) when the user has not picked an end date. */
export const RECURRENCE_END_DEFAULTS = {
  daily: { amount: 1, unit: 'month' },
  weekly: { amount: 3, unit: 'months' },
  monthly: { amount: 1, unit: 'year' },
}

/**
 * YYYY-MM-DD default end date for a recurrence frequency, based on series start date.
 */
export function defaultRecurrenceEndDate (startDate, frequency, timezone) {
  const spec = RECURRENCE_END_DEFAULTS[frequency]
  if (!spec || !startDate) {
    return null
  }
  const tz = timezone || moment.tz.guess()
  const start = moment.tz(startDate, 'YYYY-MM-DD', tz)
  if (!start.isValid()) {
    return null
  }
  return start.clone().add(spec.amount, spec.unit).format('YYYY-MM-DD')
}

/** Last instant of the end date in display TZ, as UTC ISO Z (recurrence.until). */
export function recurrenceUntilFromEndDate (endDate, timezone) {
  const tz = timezone || moment.tz.guess()
  const end = moment.tz(endDate, 'YYYY-MM-DD', tz).endOf('day')
  if (!end.isValid()) {
    return null
  }
  return end.utc().format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z'
}

/** Parse recurrence.until to a date-picker value (YYYY-MM-DD) in display TZ. */
export function recurrenceEndDateFromUntil (untilIso, timezone) {
  if (!untilIso) {
    return null
  }
  const tz = timezone || moment.tz.guess()
  const parsed = moment.utc(untilIso).tz(tz)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null
}

/**
 * Expand a scheduler event into calendar instances within [windowStart, windowEnd].
 * Uses the scheduled window (start_time/end_time duration), not pre/post-roll.
 */
export function expandEventOccurrences (event, windowStart, windowEnd, timezone) {
  const recurrence = event.recurrence || { frequency: 'none' }
  const frequency = recurrence.frequency || 'none'
  const tz = timezone || moment.tz.guess()

  const anchorStart = moment.utc(event.start_time).tz(tz)
  const anchorEnd = moment.utc(event.end_time).tz(tz)
  if (!anchorStart.isValid() || !anchorEnd.isValid()) {
    return []
  }
  const durationMs = anchorEnd.diff(anchorStart)

  const interval = recurrence.interval || 1
  let until = windowEnd.clone()
  if (recurrence.until) {
    const untilM = moment.utc(recurrence.until).tz(tz)
    if (untilM.isValid() && untilM.isBefore(until)) {
      until = untilM
    }
  }
  const maxCount = recurrence.count != null ? parseInt(recurrence.count, 10) : null

  const instances = []

  function pushInstance (occStart) {
    const occEnd = occStart.clone().add(durationMs, 'milliseconds')
    if (occEnd.isBefore(windowStart) || occStart.isAfter(windowEnd)) {
      return
    }
    instances.push({
      start: occStart.clone(),
      end: occEnd,
      startUtc: occStart.clone().utc().format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z',
      endUtc: occEnd.clone().utc().format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z',
    })
  }

  if (frequency === 'none') {
    pushInstance(anchorStart)
    return instances
  }

  let currentStart = anchorStart.clone()
  let count = 0
  const guardLimit = 5000

  while (
    currentStart.isSameOrBefore(until) &&
    currentStart.isSameOrBefore(windowEnd) &&
    count < guardLimit
  ) {
    if (maxCount != null && !isNaN(maxCount) && count >= maxCount) {
      break
    }

    if (frequency === 'daily') {
      pushInstance(currentStart)
      count += 1
      currentStart = currentStart.clone().add(interval, 'days')
    } else if (frequency === 'weekly') {
      const weekdays = recurrence.by_weekday && recurrence.by_weekday.length
        ? recurrence.by_weekday
        : [anchorStart.day()]
      for (let dayOffset = 0; dayOffset < 7 * interval; dayOffset += 1) {
        const candidate = currentStart.clone().add(dayOffset, 'days')
        if (weekdays.indexOf(candidate.day()) === -1) {
          continue
        }
        if (candidate.isAfter(until)) {
          break
        }
        pushInstance(candidate)
        count += 1
        if (maxCount != null && !isNaN(maxCount) && count >= maxCount) {
          return instances
        }
      }
      currentStart = currentStart.clone().add(interval, 'weeks')
    } else if (frequency === 'monthly') {
      pushInstance(currentStart)
      count += 1
      currentStart = currentStart.clone().add(interval, 'months')
    } else {
      break
    }
  }

  return instances
}

export function expandSchedulerEventsForCalendar (events, windowStart, windowEnd, timezone) {
  const expanded = []
  for (const evt of events || []) {
    if (evt.series_id) {
      const anchorStart = moment.utc(evt.start_time).tz(timezone || moment.tz.guess())
      const anchorEnd = moment.utc(evt.end_time).tz(timezone || moment.tz.guess())
      if (anchorStart.isValid() && anchorEnd.isValid()) {
        expanded.push({
          event: evt,
          instance: {
            start: anchorStart,
            end: anchorEnd,
            startUtc: evt.start_time,
            endUtc: evt.end_time,
          },
          recurring: Boolean(evt.series_recurrence && evt.series_recurrence.frequency !== 'none'),
        })
      }
      continue
    }
    const instances = expandEventOccurrences(evt, windowStart, windowEnd, timezone)
    const recurring = (evt.recurrence && evt.recurrence.frequency) !== 'none' &&
      evt.recurrence.frequency !== undefined
    for (const inst of instances) {
      expanded.push({
        event: evt,
        instance: inst,
        recurring,
      })
    }
  }
  return expanded
}

export function recurrenceSummary (recurrence) {
  if (!recurrence || !recurrence.frequency || recurrence.frequency === 'none') {
    return 'One time'
  }
  const interval = recurrence.interval || 1
  const labels = {
    daily: interval === 1 ? 'Daily' : 'Every ' + interval + ' days',
    weekly: interval === 1 ? 'Weekly' : 'Every ' + interval + ' weeks',
    monthly: interval === 1 ? 'Monthly' : 'Every ' + interval + ' months',
  }
  let text = labels[recurrence.frequency] || recurrence.frequency
  if (recurrence.until) {
    text += ' until ' + moment.utc(recurrence.until).format('lll')
  }
  if (recurrence.count) {
    text += ' (' + recurrence.count + ' occurrences)'
  }
  return text
}
